<template>
  <div :class="layoutLevelOneCssClass">
    <AddUserModal
      v-if="showAddUserModal"
      :company="company"
      :role="currentAddUserRole"
      :sso-entities="ssoEntities"
      :typeahead-users="typeaheadUsers"
      @close-add-modal="closeAddModal"
      @refresh-users="refreshUsers"
    />
    <AddGroupModal
      v-if="showAddGroupModal"
      :typeahead-groups="typeaheadGroups"
      @close-add-modal="closeAddModal"
      @refresh-users="refreshUsers"
    />
    <div v-if="loaded" :class="layoutLevelTwoCssClass">
      <!-- Tab Navigation -->
      <div
        class="
          tw-border-solid
          tw-border-b
          tw-border-t-0
          tw-border-l-0
          tw-border-r-0
          tw-border-sf-gray-lighter
          tw-border-b-[2px]
        "
      >
        <nav
          class="tw-flex tw-space-x-4 -mb-px mb-2"
          aria-label="User access tabs"
        >
          <a
            v-for="tab in tabs"
            :key="tab.id"
            href="#"
            role="button"
            :class="[
              'tw-no-underline hover:tw-no-underline active:tw-no-underline focus:tw-no-underline tw-text-sf-neutral-dark-1 tw-whitespace-nowrap tw-py-2 tw-px-1 tw-border-b-2 tw-text-sm',
              activeTab === tab.id
                ? 'tw-font-medium tw-border-sf-primary tw-border-solid tw-border-b tw-border-l-0 tw-border-r-0 tw-border-t-0 tw--mb-[2px] tw-border-b-[3px]'
                : '',
            ]"
            @click.prevent="activeTab = tab.id"
          >
            {{ tab.label }}
            <span
              v-if="tab.count !== undefined"
              :class="[
                'tw-ml-1 tw-rounded-full tw-py-0.5 tw-px-2 tw-text-xs',
                activeTab === tab.id
                  ? 'tw-bg-sf-primary tw-text-white'
                  : 'tw-bg-sf-snow-dark',
              ]"
            >
              {{ tab.count }}
            </span>
          </a>
        </nav>
      </div>

      <div :class="layoutLevelThreeCssClass">
        <!-- Staff Users Tab Content -->
        <div v-show="activeTab === 'staff'">
          <!-- Staff users section -->
          <div v-if="canShowFirmUsers">
            <div
              class="tw-flex tw-items-center tw-justify-between tw-mt-6 tw-mb-2"
            >
              <div class="tw-text-sm tw-font-bold">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.staff_users.multi_file_access',
                    { count: staffAccessCount }
                  )
                }}
              </div>
            </div>
            <div class="sf-below-2">
              <div v-if="canEditFirmUsers">
                <div class="sf-level" data-testid="visible-for-staff-checkbox">
                  <SfCheckbox
                    v-model="company.visibleForStaff"
                    class="visible-for-staff"
                    :label="
                      I18n.t(
                        'wizard_v2.companies.set_user_access.staff_users.visible_for_all_staff_count',
                        { count: staffAccessCount }
                      )
                    "
                    @click="toggleVisiblityForContributorsWithConfirm"
                  ></SfCheckbox>
                </div>
              </div>
            </div>
            <hr class="tw-border-b tw-border-sf-gray-light tw-mt-2 tw-mb-4" />
          </div>
          <!-- Specific file access section -->
          <div>
            <div class="sf-level sf-level-between tw-mb-1">
              <div class="tw-text-sm tw-font-bold">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.staff_users.specific_file_access',
                    { count: filteredFirmRoles.length }
                  )
                }}
              </div>
              <a
                v-if="canEditFirmUsers && filteredFirmRoles.length > 0"
                class="sf-btn sf-btn-small"
                @click.prevent="openAddUserModal('firm')"
                >+ {{ I18n.t('user.add_staff_contributor') }}</a
              >
            </div>

            <div v-if="filteredFirmRoles.length > 0" class="sf-tab tw-mb-2">
              <div
                v-for="role in filteredFirmRoles"
                :key="`${role.userId}-${role.role}`"
                class="sf-tab-item sf-hover"
              >
                <EditUserModal
                  v-if="role.userId"
                  v-show="false"
                  :ref="'editModal' + role.userId"
                  :can-edit-external-contributors="canEditExternalContributors"
                  :can-edit-firm-users="canEditFirmUsers"
                  :company-name="company.name"
                  :firm-supports-admin-users="company.firmSupportsAdminUsers"
                  :user="role"
                  @refresh-users="refreshUsers"
                ></EditUserModal>
                <div class="sf-level sf-level-between">
                  <div class="sf-level">
                    <span class="tw-w-48"
                      ><i class="fa fa-user tw-mr-2"></i
                      >{{ role.userName }}</span
                    >
                    <span class="sf-c-text-light">{{ role.userEmail }}</span>
                  </div>
                  <div class="sf-level">
                    <a
                      v-if="
                        role.userId &&
                        canEditFirmUsers &&
                        role.userId !== currentUserId
                      "
                      class="fa fa-edit tw-cursor-pointer"
                      @click.prevent="openEditUserModal(role.userId)"
                    >
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="
                tw-text-center tw-bg-gray-50 tw-rounded-lg tw-py-14 tw-mb-2
              "
            >
              <img
                :src="$imagesRequire('wizard/no_user_added.svg')"
                class="tw-mx-auto tw-h-24 tw-w-24"
              />
              <h3 class="tw-text-base tw-font-normal tw-text-sf-gray-light">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.staff_users.no_staff_contributors_added_yet'
                  )
                }}
              </h3>
              <div>
                <a
                  v-if="canEditFirmUsers"
                  class="sf-btn tw-p-1 tw-border-x-2 tw-text-sf-gray-dark"
                  @click.prevent="openAddUserModal('firm')"
                  >+ {{ I18n.t('user.add_staff_contributor') }}</a
                >
              </div>
            </div>

            <div class="tw-mt-2">
              <i class="fa fa-info-circle tw-mr-2 tw-text-sf-primary"></i>
              <span class="tw-text-sf-gray-light">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.staff_users.staff_contributor_info'
                  )
                }}</span
              >
            </div>
            <hr
              v-if="allowedActions.showGroups"
              class="tw-border-b tw-border-sf-neutral-lighter-1 tw-my-6"
            />
          </div>

          <!-- Groups section (moved inside Staff tab) -->
          <div v-if="allowedActions.showGroups" class="tw-mb-4">
            <div class="sf-level sf-level-between tw-mb-1">
              <div class="tw-text-sm tw-font-bold">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.staff_users.groups',
                    { count: totalGroupsUsers }
                  )
                }}
              </div>
              <a
                v-if="allowedActions.addGroup && groups.length > 0"
                class="sf-btn sf-btn-small add-group"
                @click.prevent="openAddGroupModal"
                >+ {{ I18n.t('user.add_group') }}</a
              >
            </div>

            <div v-if="groups.length > 0" class="sf-tab">
              <div
                v-for="group in groups"
                :key="group.id"
                class="sf-tab-item sf-hover"
              >
                <div class="sf-level sf-level-between tw-items-center">
                  <div class="sf-level tw-items-center">
                    <span class="tw-w-48"
                      ><i class="fa fa-user-group tw-mr-2"></i
                      >{{ group.name }}</span
                    >
                    <span class="sf-c-text-light">
                      {{
                        I18n.t(
                          'wizard_v2.companies.set_user_access.staff_users.group_users_count',
                          { count: group.usersCount }
                        )
                      }}
                    </span>
                  </div>
                  <a
                    v-if="allowedActions.addGroup"
                    href="#"
                    class="tw-text-lg tw-text-red-500 hover:tw-text-red-700"
                    @click.prevent="deleteGroup(group.id)"
                    ><i class="fa fa-remove tw-cursor-pointer"></i
                  ></a>
                </div>
              </div>
            </div>
            <div
              v-else
              class="tw-text-center tw-bg-gray-50 tw-rounded-lg tw-py-14"
            >
              <img
                :src="$imagesRequire('wizard/no_user_added.svg')"
                class="tw-mx-auto tw-h-24 tw-w-24"
              />
              <h3 class="tw-text-base tw-font-normal tw-text-sf-gray-light">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.staff_users.no_groups_added_yet'
                  )
                }}
              </h3>
              <div>
                <a
                  v-if="allowedActions.addGroup"
                  class="
                    sf-btn
                    tw-p-1 tw-border-x-2 tw-text-sf-gray-dark
                    add-group
                  "
                  @click.prevent="openAddGroupModal"
                  >+ {{ I18n.t('user.add_group') }}</a
                >
              </div>
            </div>
          </div>
        </div>
        <!-- Pulse Users Tab Content -->
        <div v-show="activeTab === 'pulse'">
          <!-- Pulse Template Section -->
          <div id="pulse-template" class="tw-mb-8 tw-mt-6">
            <div class="tw-text-sm tw-font-bold tw-mb-4">
              {{
                I18n.t(
                  'wizard_v2.companies.set_user_access.pulse_users.pulse_template'
                )
              }}
            </div>

            <div class="tw-mb-4">
              <!-- Radio button container -->
              <div
                class="
                  tw-flex
                  tw-flex-col
                  tw-rounded-lg
                  tw-overflow-hidden
                  tw-rounded-sf-gray-lighter
                  tw-border
                  tw-border-solid
                  tw-border-sf-gray-lighter
                "
              >
                <!-- Pulse Preset Options -->
                <label
                  v-for="(preset, index) in pulsePresetOptions"
                  :key="preset.id || 'none'"
                  :for="'preset-' + (preset.id || 'none')"
                  class="
                    sf-form-check sf-form-check-rounded
                    tw-items-center tw-py-3 tw-px-4 tw-cursor-pointer
                  "
                  :class="{
                    'tw-border-t tw-border-l-0 tw-border-r-0 tw-border-b-0 tw-border-solid tw-border-sf-gray-lighter':
                      index > 0,
                  }"
                >
                  <input
                    :id="'preset-' + (preset.id || 'none')"
                    v-model="selectedPulsePreset"
                    type="radio"
                    :name="preset.id ? 'company[pulse_preset_id]' : null"
                    :value="preset.id"
                    class="sf-hidden"
                  />
                  <span class="sf-form-check-content tw-flex">
                    <span class="sf-form-check-indicator"> </span>
                    <span class="sf-inline-block tw-pl-2">{{
                      preset.name
                    }}</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <hr class="tw-border-b tw-border-sf-gray-light tw-mt-2 tw-mb-4" />

          <!-- Pulse Users Section -->
          <div class="tw-mb-2 tw-mt-6">
            <!-- Pulse users section header - always visible -->
            <div class="sf-level sf-level-between tw-mb-1">
              <div class="tw-text-sm tw-font-bold">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.pulse_users.pulse_users',
                    { count: customerRoles.length }
                  )
                }}
              </div>
              <a
                v-if="allowedActions.editUsers && customerRoles.length > 0"
                class="sf-btn sf-btn-small"
                @click.prevent="openAddUserModal('customer')"
                >+ {{ I18n.t('user.add_pulse_access_btn') }}</a
              >
            </div>

            <div v-if="customerRoles.length > 0" class="sf-tab">
              <div
                v-for="role in customerRoles"
                :key="`${role.userId}-${role.role}`"
                class="sf-tab-item sf-hover"
              >
                <EditUserModal
                  v-if="role.userId"
                  v-show="false"
                  :ref="'editModal' + role.userId"
                  :can-edit-external-contributors="canEditExternalContributors"
                  :can-edit-firm-users="canEditFirmUsers"
                  :company-name="company.name"
                  :firm-supports-admin-users="company.firmSupportsAdminUsers"
                  :user="role"
                  @refresh-users="refreshUsers"
                ></EditUserModal>
                <div class="sf-level sf-level-between">
                  <div class="sf-level">
                    <span class="tw-w-48">
                      <i class="fa fa-user tw-mr-2"></i>
                      <a
                        v-if="isCurrentUserFirmAdmin && role.userId"
                        :href="userUrl(role.userId)"
                        class="
                          tw-text-sf-primary
                          hover:tw-text-sf-primary-dark hover:tw-underline
                        "
                        >{{ role.userName }}</a
                      >
                      <span v-else>{{ role.userName }}</span>
                      <span
                        v-if="role.userId === currentUserId"
                        class="tw-ml-1 tw-text-sm tw-text-gray-500"
                        >{{ I18n.t('user.you') }}</span
                      >
                      <span
                        v-if="role.usersAdmin && company.firmSupportsAdminUsers"
                        class="tw-ml-2 tw-text-sm tw-text-gray-500"
                        >{{ I18n.t('user.user_administrator') }}</span
                      >
                    </span>
                    <span class="sf-c-text-light">{{ role.userEmail }}</span>
                  </div>
                  <div class="sf-level">
                    <a
                      v-if="
                        allowedActions.editUsers &&
                        role.userId !== currentUserId
                      "
                      class="fa fa-edit tw-cursor-pointer"
                      @click.prevent="openEditUserModal(role.userId)"
                    ></a>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="tw-text-center tw-bg-gray-50 tw-rounded-lg tw-py-14"
            >
              <img
                :src="$imagesRequire('wizard/no_user_added.svg')"
                class="tw-mx-auto tw-h-24 tw-w-24"
              />
              <h3 class="tw-text-base tw-font-normal tw-text-sf-gray-light">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.pulse_users.no_pulse_users_added_yet'
                  )
                }}
              </h3>
              <div>
                <a
                  v-if="allowedActions.editUsers"
                  class="sf-btn tw-border-x-2 tw-text-sf-gray-dark"
                  @click.prevent="openAddUserModal('customer')"
                  >+ {{ I18n.t('user.add_pulse_access_btn') }}</a
                >
              </div>
            </div>
          </div>
        </div>
        <!-- External Users Tab Content -->
        <div v-show="activeTab === 'external'">
          <!-- External users section -->
          <div
            v-if="!canShowExternalContributors && !canEditExternalContributors"
          >
            <SfBannerPage
              :variant="bannerVariants.info"
              has-long-text
              is-full-width
              class="tw-my-8"
            >
              {{
                I18n.t(
                  'wizard_v2.companies.set_user_access.external_users.no_access_message'
                )
              }}
            </SfBannerPage>
          </div>
          <div v-if="canShowExternalContributors" class="tw-mb-2 tw-mt-6">
            <!-- External users section header - always visible -->
            <SfBannerPage
              v-if="canEditExternalContributors && showExternalIsPaid()"
              :title="
                externalContributorRoles.length == 0
                  ? I18n.t('user.external_activation_title')
                  : undefined
              "
              :variant="bannerVariants.info"
              class="sf-below-2"
              :has-long-text="true"
              is-full-width
            >
              {{ I18n.t('user.external_activation_text') }}
            </SfBannerPage>
            <div class="sf-level sf-level-between tw-mb-1">
              <div class="tw-text-sm tw-font-bold">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.external_users.external_users',
                    { count: externalContributorRoles.length }
                  )
                }}
              </div>
              <a
                v-if="
                  canEditExternalContributors &&
                  externalContributorRoles.length > 0
                "
                class="sf-btn sf-btn-small"
                @click.prevent="openAddUserModal('external')"
                >{{ I18n.t('user.add_external_contributor') }}</a
              >
            </div>

            <!-- List of external users when they exist -->
            <div v-if="externalContributorRoles.length > 0" class="sf-tab">
              <div
                v-for="role in externalContributorRoles"
                :key="`${role.userId}-${role.role}`"
                class="sf-tab-item sf-hover"
              >
                <EditUserModal
                  v-if="role.userId"
                  v-show="false"
                  :ref="'editModal' + role.userId"
                  :can-edit-external-contributors="canEditExternalContributors"
                  :can-edit-firm-users="canEditFirmUsers"
                  :company-name="company.name"
                  :firm-supports-admin-users="company.firmSupportsAdminUsers"
                  :user="role"
                  @refresh-users="refreshUsers"
                ></EditUserModal>
                <div class="sf-level sf-level-between">
                  <div class="sf-level">
                    <span class="tw-w-48">
                      <i class="fa fa-user tw-mr-2"></i>
                      <a
                        v-if="isCurrentUserFirmAdmin && role.userId"
                        :href="userUrl(role.userId)"
                        class="
                          tw-text-sf-primary
                          hover:tw-text-sf-primary-dark hover:tw-underline
                        "
                        >{{ role.userName }}</a
                      >
                      <span v-else>{{ role.userName }}</span>
                      <span
                        v-if="role.userId === currentUserId"
                        class="tw-ml-1 tw-text-sm tw-text-gray-500"
                        >{{ I18n.t('user.you') }}</span
                      >
                      <span
                        v-if="role.usersAdmin && company.firmSupportsAdminUsers"
                        class="tw-ml-2 tw-text-sm tw-text-gray-500"
                        >{{ I18n.t('user.user_administrator') }}</span
                      >
                    </span>
                    <span class="sf-c-text-light">{{ role.userEmail }}</span>
                  </div>
                  <div class="sf-level">
                    <a
                      v-if="
                        canEditExternalContributors &&
                        role.userId !== currentUserId
                      "
                      class="fa fa-edit tw-cursor-pointer"
                      @click.prevent="openEditUserModal(role.userId)"
                    ></a>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="tw-text-center tw-bg-gray-50 tw-rounded-lg tw-py-14"
            >
              <img
                :src="$imagesRequire('wizard/no_user_added.svg')"
                class="tw-mx-auto tw-h-24 tw-w-24"
              />
              <h3 class="tw-text-base tw-font-normal tw-text-sf-gray-light">
                {{
                  I18n.t(
                    'wizard_v2.companies.set_user_access.external_users.no_external_contributors_added_yet'
                  )
                }}
              </h3>
              <div>
                <a
                  v-if="canEditExternalContributors"
                  class="sf-btn tw-border-x-2 tw-text-sf-gray-dark"
                  @click.prevent="openAddUserModal('external')"
                  >+ {{ I18n.t('user.add_external_contributor') }}</a
                >
              </div>
            </div>
          </div>
        </div>
        <!-- End of tab content -->

        <hr class="tw-border-b tw-border-sf-gray-light tw-mt-4 tw-mb-4" />
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api/index'
import AddUserModal from './add-user-modal'
import EditUserModal from './edit-user-modal'
import AddGroupModal from './add-group-modal'
import SfCheckbox from '@app/shared/vue/components/sf-checkbox'
import SfBannerPage from '@app/shared/vue/components/sf-banner/sf-banner-page.vue'
import { bannerVariants } from '@app/shared/types/banner'
import { showExternalIsPaid, userUrl } from '@app/shared/company-users/utils'

const CompanyUsersV2 = {
  components: {
    AddUserModal,
    EditUserModal,
    AddGroupModal,
    SfCheckbox,
    SfBannerPage,
  },

  props: {
    isRelaxedLayout: {
      type: Boolean,
      default: false,
    },
    pulsePresets: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      // Tab navigation
      activeTab: 'staff', // Default active tab
      tabs: [
        {
          id: 'staff',
          label: I18n.t(
            'wizard_v2.companies.set_user_access.staff_users.title'
          ),
          count: 0,
        },
        {
          id: 'pulse',
          label: I18n.t(
            'wizard_v2.companies.set_user_access.pulse_users.title'
          ),
          count: 0,
        },
        {
          id: 'external',
          label: I18n.t(
            'wizard_v2.companies.set_user_access.external_users.title'
          ),
          count: 0,
        },
      ],
      ssoEntities: [],
      groups: [],
      company: {},
      loaded: false,
      currentUserId: parseInt(window.Silverfin.currentUserId(), 10),
      selectedPulsePreset: null,
      currentAddUserRole: '',
      showAddUserModal: false,
      showAddGroupModal: false,
      firmRoles: [],
      customerRoles: [],
      externalContributorRoles: [],
      typeaheadUsers: [],
      typeaheadGroups: [],
      allowedActions: {},
      layoutLevelOneCssClass: 'sf-layer-1',
      layoutLevelTwoCssClass: 'sf-layer-2',
      layoutLevelThreeCssClass: 'sf-layer-3',
      bannerVariants,
    }
  },

  computed: {
    pulsePresetOptions() {
      const noneOption = {
        id: null,
        name: this.I18n.t(
          'wizard_v2.companies.set_user_access.pulse_users.no_pulse_template'
        ),
      }

      if (!this.pulsePresets || this.pulsePresets.length === 0) {
        return [noneOption]
      }

      // Find and separate the 'Default' preset to ensure it's always second
      const defaultPreset = this.pulsePresets.find((p) => p.name === 'Default')
      const otherPresets = this.pulsePresets.filter((p) => p.name !== 'Default')

      const orderedPresets = [noneOption]
      if (defaultPreset) {
        orderedPresets.push(defaultPreset)
      }
      orderedPresets.push(...otherPresets)

      return orderedPresets
    },

    filteredFirmRoles() {
      if (this.company.visibleForStaff) {
        return this.firmRoles.filter(
          (role) => role.userId !== this.currentUserId
        )
      }
      return this.firmRoles
    },

    totalGroupsUsers() {
      return this.groups.reduce(
        (sum, group) => sum + (group.usersCount || 0),
        0
      )
    },

    totalStaffUsers() {
      const multiFile = this.company.visibleForStaff ? this.staffAccessCount : 0
      return multiFile + this.totalGroupsUsers + this.filteredFirmRoles.length
    },

    allUserRoles() {
      return this.firmRoles
        .concat(this.customerRoles)
        .concat(this.externalContributorRoles)
    },

    rolesWillBeRemoved() {
      return this.firmRoles
        .filter(
          (role) =>
            role.userStaffAccess &&
            !role.usersAdmin &&
            role.userId !== this.currentUserId
        )
        .map((role) => role.userName)
        .join(', ')
    },

    currentUserCompanyRole() {
      const currentUserRole = this.allUserRoles.find(
        (role) => role.userId === this.currentUserId
      )
      return currentUserRole ? currentUserRole.role : ''
    },

    isCurrentUserFirmAdmin() {
      return window.Silverfin.isCurrentUserAdmin()
    },

    canShowFirmUsers() {
      return this.currentUserCompanyRole === 'firm'
    },

    canEditFirmUsers() {
      return this.allowedActions.editUsers && this.canShowFirmUsers
    },

    canShowExternalContributors() {
      return this.canShowFirmUsers || this.currentUserCompanyRole === 'external'
    },

    canEditExternalContributors() {
      return this.allowedActions.editUsers && this.canShowExternalContributors
    },
  },

  mounted() {
    if (this.$attrs.isRelaxedLayout === 'true') {
      this.layoutLevelOneCssClass = 'sf-columns'
      this.layoutLevelTwoCssClass = 'sf-col-9 sf-col-9-tablet sf-col-12-mobile'
      this.layoutLevelThreeCssClass = 'sf-below-2'
    }
    this.getCompanyUsers()
  },

  updated() {
    // Update tab counts when data changes
    this.updateTabCounts()
  },

  methods: {
    updateTabCounts() {
      this.tabs[0].count = this.totalStaffUsers
      this.tabs[1].count = this.customerRoles.length
      this.tabs[2].count = this.externalContributorRoles.length
    },

    getCompanyUsers() {
      api.getUsers().then((response) => {
        this.resetExistingRoles()
        this.company = response.company
        this.staffAccessCount = response['staffAccessCount']
        this.firmRoles = response['firmRoles']
        this.customerRoles = response['customerRoles']
        this.externalContributorRoles = response['externalContributorRoles']
        this.allowedActions = response['allowedActions']
        this.groups = response['groups']
        this.ssoEntities = response['ssoEntities']
        this.loaded = true

        // Initialize pulse preset selection based on company data
        if (this.company && this.company.pulse_preset_id) {
          this.selectedPulsePreset = this.company.pulse_preset_id
        } else {
          this.selectedPulsePreset = null
        }

        this.updateTabCounts()
      })
    },

    getTypeaheadUsers() {
      api.getTypeaheadUsers().then((response) => {
        // Filter to eliminate already assigned users on typeahead list
        const userIds = this.allUserRoles.map((u) => u['userId'])
        this.typeaheadUsers = response['users'].filter(
          (user) =>
            !userIds.includes(user.id) &&
            !(this.company.visibleForStaff && user.staffAccess)
        )
      })
    },

    getTypeaheadGroups() {
      api.getTypeaheadGroups().then((response) => {
        // Filter to eliminate already assigned groups on typeahead list
        const groupIds = this.groups.map((u) => u['id'])
        this.typeaheadGroups = response.filter(
          (group) => !groupIds.includes(group.id)
        )
      })
    },

    openAddUserModal(role) {
      this.getTypeaheadUsers()
      this.currentAddUserRole = role
      this.showAddUserModal = true
    },

    openAddGroupModal() {
      this.getTypeaheadGroups()
      this.showAddGroupModal = true
    },

    openEditUserModal(index) {
      const modal = this.$refs['editModal' + index]
      if (modal && modal[0] && modal[0].$el) {
        modal[0].$el.style.display = 'block'
      }
    },

    closeAddModal() {
      this.showAddUserModal = false
      this.showAddGroupModal = false
    },

    resetExistingRoles() {
      this.firmRoles = []
      this.customerRoles = []
      this.externalContributorRoles = []
    },

    refreshUsers() {
      this.loaded = false
      this.currentAddUserRole = ''
      this.closeAddModal()
      this.getCompanyUsers()
      this.getTypeaheadUsers()
    },

    toggleCompanyVisible() {
      this.company.visibleForStaff = !this.company.visibleForStaff
    },

    toggleVisiblityForContributors() {
      api
        .companyToggleVisibility()
        .catch(() => this.toggleCompanyVisible())
        .then(
          () =>
            this.company.visibleForStaff &&
            this.rolesWillBeRemoved !== '' &&
            this.refreshUsers()
        )
    },

    toggleVisiblityForContributorsWithConfirm() {
      // warning! this method is called _before_ the toggling of this.company.visibleForStaff happens
      // due to the checkbox clicking. That's why !this.company.visibleForStaff is checked
      // as we want to show the confirm screen only if this.company.visibleForStaff is false
      // and is toggled to true.
      const message =
        this.rolesWillBeRemoved !== '' &&
        I18n.t('company.some_roles_will_be_removed_warning', {
          users: this.rolesWillBeRemoved,
        })

      if (!this.company.visibleForStaff) {
        const escapedCompanyName =
          window.Silverfin.GlobalConfirmationModal.escape(this.company.name)
        window.sfConfirm({
          question: I18n.t('company.make_visible_to_all_title_html', {
            company_name: escapedCompanyName,
            count: this.staffAccessCount,
          }),
          message: message,
          confirmLabel: I18n.t('company.role_removed_confirm_btn'),
          cancelLabel: I18n.t('js.global_confirmation_modal.default.cancel'),
          onConfirm: this.toggleVisiblityForContributors,
          onCancel: this.toggleCompanyVisible,
          options: {
            message_class: 'sf-bg-snow sf-c-text',
            confirm_class: 'sf-btn-warning',
          },
        })
      } else {
        this.toggleVisiblityForContributors()
      }
    },

    deleteGroup(id) {
      api.deleteGroup(id).then(() => {
        this.getCompanyUsers()
      })
    },

    userUrl(userId) {
      return userUrl(userId)
    },

    showExternalIsPaid() {
      return showExternalIsPaid(this.company, this.externalContributorRoles)
    },
  },
}

export default CompanyUsersV2
</script>
